import { Language } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { analyticsService } from '../../../../application/AnalyticsService';
import { BinTypes } from '../../../../domain/analytics/BinTypes';
import { StopAnalyticsDateChartData } from '../../../../domain/analytics/stopAnalyticsDateChartData/StopAnalyticsDateChartData';
import { useRetrieveActiveLanguage } from '../../../../hooks/translations/useRetrieveActiveLanguage';
import { getLabelByBinType } from './useAnalyticsFilters';

interface UseRetrieveAnalyticsStopsData {
  date: string;
  dateLabel: string;
  completed_stops: number;
  incomplete_stops?: number;
  canceled_stops: number;
  pending_stops: number;
}

type UseRetrieveAnalyticsStopsResponse = ServiceResponse<() => void, UseRetrieveAnalyticsStopsData[]>;

// Hooks act as 'Adapter' layer.
export const useRetrieveAnalyticsStops = ({
  startDate,
  endDate,
  binType,
  tasksEnabled = false,
}: {
  startDate: Date;
  endDate: Date;
  binType: BinTypes;
  tasksEnabled?: boolean;
}): UseRetrieveAnalyticsStopsResponse => {
  const { activeLanguage } = useRetrieveActiveLanguage();

  const [stopsResponse, setStopsResponse] = useState<StopAnalyticsDateChartData[] | undefined>();
  const [stopsData, setStopsData] = useState<UseRetrieveAnalyticsStopsData[] | undefined>();

  const {
    isInitialLoading: isLoading,
    isError,
    isSuccess,
    isFetching,
    isRefetching,
    refetch,
  } = useQuery(
    [`drivers_analytics_stops`, startDate, endDate, binType],
    () => analyticsService.getAnalyticsStops({ startDate, endDate, binType }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        setStopsResponse(response);
      },
    }
  );

  useEffect(() => {
    if (stopsResponse) {
      const locale = activeLanguage ?? Language.getDefaultLanguage().value;

      const newData: UseRetrieveAnalyticsStopsData[] = stopsResponse.map(
        (stops) =>
          ({
            date: stops.date,
            dateLabel: getLabelByBinType(stops.date, binType, locale),
            completed_stops: stops.value.completed_stops,
            ...(tasksEnabled
              ? {
                  incomplete_stops: stops.value.incomplete_stops,
                }
              : {}),
            canceled_stops: stops.value.canceled_stops,
            pending_stops: stops.value.pending_stops,
          } as UseRetrieveAnalyticsStopsData)
      );
      setStopsData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsResponse]);

  return {
    error: isError,
    isLoading: isLoading || isFetching || isRefetching,
    success: isSuccess,
    run: refetch,
    data: stopsData,
  };
};
